import type { ElementType, ReactNode } from 'react'
import { Botao } from '@ui/glass/Botao'

/**
 * Um botão que é só ícone: o nome vive no `aria-label`, e não na tela.
 *
 * ## `Botao`, e não uma âncora pintada à mão
 *
 * É a pílula do sistema (decisão 232) — ação de tela, não controle de linha.
 * `flat` por padrão, então não gasta desfoque do orçamento do `Glass`. Redondo
 * porque o raio já é 9999 e a largura vira a altura: fica quadrado, do tamanho
 * do avatar que costuma estar ao lado.
 *
 * ## O `px-0` tinha de ir por `style`, e isso foi medido
 *
 * `Botao` declara `px-4`, e entre duas utilities do Tailwind quem decide é a
 * ordem da folha gerada: `px-0` escrito depois **perdia**. O computado ficava
 * em 16px de cada lado, sobravam 10px de caixa dentro dos 44px, e o `<svg>` —
 * item flex, portanto encolhível — desenhava a 10px em vez de 24. Medido em
 * produção com `getBoundingClientRect`, não deduzido.
 *
 * O ícone que parecia certo era o único dentro de um `<span>` (o `+`, que gira):
 * ali o `<svg>` não é item flex e mantinha os 24px. O acerto era acidente.
 *
 * ## O nome é obrigatório, e por isso é um parâmetro
 *
 * Ícone sem nome acessível é um botão mudo. Aqui não dá para esquecer: o
 * `rotulo` não tem padrão, e some no `aria-label` **e** no `title` — o segundo
 * é o resgate de quem tem mouse e não reconheceu o desenho. Num toque o `title`
 * não aparece, e é por isso que ele é um extra, e não a resposta.
 *
 * ## Ícone puro serve para CONVENÇÃO e para ATALHO
 *
 * `TabBar` recusa ícone puro com um argumento bom: *"'etiqueta' e 'lista' não
 * dizem sozinhos o que são, e quem abre o app uma vez por semana reaprende toda
 * vez"*. Ele vale onde vale: a barra de abas é a única porta das quatro seções,
 * e o desenho sozinho decidiria se a pessoa acha a seção. Um cabeçalho é outro
 * caso — poucos controles, no mesmo lugar todo dia, ao lado de um título que
 * diz onde se está.
 */
export function BotaoDeIcone({
  rotulo,
  as,
  className = '',
  children,
  ...rest
}: {
  rotulo: string
  as?: ElementType
  className?: string
  children: ReactNode
  [prop: string]: unknown
}) {
  return (
    <Botao
      {...rest}
      as={as}
      aria-label={rotulo}
      title={rotulo}
      className={`relative w-11 ${className}`.trim()}
      style={{ paddingInline: 0 }}
    >
      {children}
    </Botao>
  )
}
